import { Helmet } from 'react-helmet-async';
import { AgGridReact } from 'ag-grid-react';
import { useState, useEffect, useRef } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import axiosInstance from '../../utils/axios';
import EditIconButton from '../../reuseable-components/DefaultButtons/EditIconButton';
import DeleteIconButton from '../../reuseable-components/DefaultButtons/DeleteIconButton';
import VendorForm from './VendorForm';
import Iconify from '../../reuseable-components/iconify/Iconify';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';

export default function VendorList() {
  const gridRef = useRef(null);

  const [vendors, setVendors] = useState([]);
  const [netTerms, setNetTerms] = useState([]);
  const [contactTypes, setContactTypes] = useState([]);
  const [editVendor, setEditVendor] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get("/vendor/vendors")
      .then(res => {
        setVendors(res.data.vendors);
        setNetTerms(res.data.netTerms);
        setContactTypes(res.data.contactTypes);
      })
      .catch(() => enqueueSnackbar('Vendor list failed', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const deleteVendor = (id) => {
    axiosInstance.post(`/vendor/deleteVendor?id=${id}`)
      .then(() => {
        setVendors(vendors => [...vendors.filter(vendor => vendor.id !== id)]);
        enqueueSnackbar('Vendor deleted', { variant: 'success' })
      })
      .catch(() => enqueueSnackbar('Vendor delete failed', { variant: 'error' }))
  };

  const columns = [
    {
      field: 'name',
      minWidth: 150,
      flex: 1
    },
    {
      field: 'accountNumber',
      width: 150,
    },
    {
      field: 'streetAddress',
      headerName: 'Address',
      minWidth: 100,
      flex: 1
    },
    {
      field: 'state',
      minWidth: 100,
    },
    {
      field: 'city',
      minWidth: 150,
    },
    {
      field: 'zip',
      headerName: 'Zip',
      minWidth: 100,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      cellRenderer: (params) => <Stack direction="row" spacing={1}>
        <EditIconButton onClick={() => { setEditVendor(params.data); setOpenForm(true); }} />
        <DeleteIconButton content={`Are you sure you want to delete ${params.data.name}?`} action={() => deleteVendor(params.data.id)} />
      </Stack>
    }
  ];

  return (<>
    <Helmet>
      <title>Vendor List | NHIDirect</title>
    </Helmet>

    <Box sx={{ height: "calc(100% - 55px)", mt: 2 }}>
      <AGGridQuickFilterWrapper gridRef={gridRef}>
        <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => { setEditVendor(null); setOpenForm(true); }} color="primary" size="small">
          <Typography>Add Vendor</Typography>
        </Button>
      </AGGridQuickFilterWrapper>
      {!loading ? <Box sx={{ height: "max(500px, 100%)" }}>
        <AgGridReact ref={gridRef} {...AgGridDefaults} columnDefs={columns} rowData={vendors} loading={loading} />
      </Box> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
      <VendorForm editVendor={editVendor} open={openForm} close={() => { setEditVendor(null); setOpenForm(false); }}
        netTerms={netTerms} contactTypes={contactTypes} setVendors={setVendors} />
    </Box>
  </>);
}
